import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-modal";
import Swal from "sweetalert2";
import { CircularProgress } from "@mui/material";
import axiosInstance from "../../config/axiosConfig";
import Actions from "../../components/courses/Actions";
import { MAX_SIZE_MB } from "../../helper";

const customModalStyles = {
  content: {
    padding: "20px",
    width: "80%",
    maxWidth: "640px",
    margin: "auto",
    borderRadius: "8px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    maxHeight: "90vh",
    overflow: "auto",
  },
};

const emptyForm = {
  name: "",
  href: "",
  menuType: "main",
  parentId: "",
  order: "",
  hideParentLink: false,
  description: "",
  contactName: "",
  contactEmail: "",
  img: "",
};

const fieldStyle = {
  width: "100%",
  padding: "8px",
  fontSize: "14px",
  borderRadius: "4px",
  border: "1px solid #ccc",
  boxSizing: "border-box",
};

const labelStyle = {
  display: "block",
  fontWeight: "bold",
  marginBottom: "5px",
};

const Office = () => {
  const [items, setItems] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [imgFile, setImgFile] = useState(null);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    setFetchLoading(true);
    try {
      const response = await axiosInstance.get("office/menu");
      setItems(response.data.data || []);
    } catch (error) {
      console.error("Error fetching offices:", error);
    }
    setFetchLoading(false);
  };

  const mainItems = useMemo(
    () =>
      items
        .filter((item) => !item.parentId)
        .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0)),
    [items]
  );

  const childrenByParent = useMemo(() => {
    const map = {};
    items
      .filter((item) => item.parentId)
      .forEach((item) => {
        const key = String(item.parentId);
        if (!map[key]) map[key] = [];
        map[key].push(item);
      });
    Object.keys(map).forEach((key) => {
      map[key].sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0));
    });
    return map;
  }, [items]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const next = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };
      if (name === "menuType" && value === "main") {
        next.parentId = "";
      }
      return next;
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > MAX_SIZE_MB) {
      alert(`File size exceeds ${MAX_SIZE_MB} MB. Please upload a smaller file.`);
      e.target.value = "";
      return;
    }
    setImgFile(file);
  };

  const appendPayload = (formDataToSend) => {
    formDataToSend.append("name", formData.name);
    formDataToSend.append("href", formData.href || "");
    formDataToSend.append("order", String(Number(formData.order || 0)));

    if (formData.menuType === "sub") {
      formDataToSend.append("parentId", formData.parentId);
      formDataToSend.append("hideParentLink", "false");
    } else {
      formDataToSend.append("parentId", "");
      formDataToSend.append("hideParentLink", formData.hideParentLink ? "true" : "false");
      formDataToSend.append("subDropSide", "left");
      formDataToSend.append("description", formData.description || "");
      formDataToSend.append("contactName", formData.contactName || "");
      formDataToSend.append("contactEmail", formData.contactEmail || "");
      if (imgFile) {
        formDataToSend.append("img", imgFile);
      }
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (formData.menuType === "sub" && !formData.parentId) {
      Swal.fire("Error", "Please select a main office for this sub menu.", "error");
      return;
    }
    setButtonLoading(true);
    try {
      const formDataToSend = new FormData();
      appendPayload(formDataToSend);
      await axiosInstance.post("office/create", formDataToSend);
      await fetchItems();
      setShowCreateModal(false);
      setFormData(emptyForm);
      setImgFile(null);
      Swal.fire("Success!", "Saved successfully!", "success");
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to create",
        "error"
      );
    }
    setButtonLoading(false);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (formData.menuType === "sub" && !formData.parentId) {
      Swal.fire("Error", "Please select a main office for this sub menu.", "error");
      return;
    }
    setButtonLoading(true);
    try {
      const formDataToSend = new FormData();
      appendPayload(formDataToSend);
      formDataToSend.append("_method", "PUT");
      await axiosInstance.post(`office/change/${editId}`, formDataToSend);
      await fetchItems();
      setShowEditModal(false);
      setFormData(emptyForm);
      setImgFile(null);
      Swal.fire("Success!", "Updated successfully!", "success");
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to update",
        "error"
      );
    }
    setButtonLoading(false);
  };

  const handleView = async (id) => {
    try {
      setShowEditModal(true);
      setEditId(id);
      setImgFile(null);
      const response = await axiosInstance.get(`office/single/${id}`);
      const data = response?.data?.data;
      const isSub = !!data?.parentId;
      setFormData({
        name: data?.name || "",
        href: data?.href || "",
        menuType: isSub ? "sub" : "main",
        parentId: data?.parentId ? String(data.parentId) : "",
        order: data?.order ?? "",
        hideParentLink: !!data?.hideParentLink,
        description: data?.description || "",
        contactName: data?.contactName || "",
        contactEmail: data?.contactEmail || "",
        img: data?.img || "",
      });
    } catch (error) {
      Swal.fire("Error", "Failed to load item", "error");
    }
  };

  const handleDelete = async (id) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, delete it!",
      });
      if (!result.isConfirmed) return;

      await axiosInstance.delete(`office/delete/${id}`);
      await fetchItems();
      Swal.fire("Deleted!", "Deleted successfully", "success");
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to delete",
        "error"
      );
    }
  };

  const parentOptionsForEdit = mainItems.filter((item) => item._id !== editId);

  const renderForm = (onSubmit, submitLabel) => (
    <form onSubmit={onSubmit}>
      <div style={{ marginBottom: "10px" }}>
        <label style={labelStyle}>Name</label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          style={fieldStyle}
        />
      </div>

      {formData.menuType === "sub" ? (
        <div style={{ marginBottom: "10px" }}>
          <label style={labelStyle}>Link (href)</label>
          <input
            type="text"
            name="href"
            value={formData.href}
            onChange={handleChange}
            placeholder="/office-page"
            style={fieldStyle}
          />
        </div>
      ) : (
        <p style={{ margin: "0 0 10px", color: "#666", fontSize: 13 }}>
          The office page link is generated automatically.
        </p>
      )}

      <div style={{ marginBottom: "10px" }}>
        <label style={labelStyle}>Type</label>
        <select
          name="menuType"
          value={formData.menuType}
          onChange={handleChange}
          style={fieldStyle}
        >
          <option value="main">Main office (Key Offices)</option>
          <option value="sub">Sub menu item</option>
        </select>
      </div>

      {formData.menuType === "main" ? (
        <>
          <div style={{ marginBottom: "10px" }}>
            <label style={labelStyle}>Order</label>
            <input
              type="number"
              name="order"
              min="0"
              value={formData.order}
              onChange={handleChange}
              style={fieldStyle}
            />
          </div>
          <div style={{ marginBottom: "10px" }}>
            <label style={{ ...labelStyle, fontWeight: "normal" }}>
              <input
                type="checkbox"
                name="hideParentLink"
                checked={formData.hideParentLink}
                onChange={handleChange}
                style={{ marginRight: 8 }}
              />
              Hide parent link (open only via sub items)
            </label>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <label style={labelStyle}>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              style={{ ...fieldStyle, height: 100 }}
            />
          </div>
          <div style={{ marginBottom: "10px" }}>
            <label style={labelStyle}>Contact Name</label>
            <input
              type="text"
              name="contactName"
              value={formData.contactName}
              onChange={handleChange}
              style={fieldStyle}
            />
          </div>
          <div style={{ marginBottom: "10px" }}>
            <label style={labelStyle}>Contact Email</label>
            <input
              type="email"
              name="contactEmail"
              value={formData.contactEmail}
              onChange={handleChange}
              style={fieldStyle}
            />
          </div>
          <div style={{ marginBottom: "10px" }}>
            <label style={labelStyle}>Image</label>
            {formData.img ? (
              <img
                src={formData.img}
                alt=""
                style={{
                  width: "100%",
                  maxHeight: 140,
                  objectFit: "cover",
                  borderRadius: 6,
                  marginBottom: 8,
                }}
              />
            ) : null}
            <input type="file" accept="image/*" onChange={handleFileChange} style={fieldStyle} />
          </div>
        </>
      ) : (
        <>
          <div style={{ marginBottom: "10px" }}>
            <label style={labelStyle}>Main office</label>
            <select
              name="parentId"
              value={formData.parentId}
              onChange={handleChange}
              required
              style={fieldStyle}
            >
              <option value="">Select main office</option>
              {(showEditModal ? parentOptionsForEdit : mainItems).map((item) => (
                <option key={item._id} value={item._id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <label style={labelStyle}>Sub menu order</label>
            <input
              type="number"
              name="order"
              min="0"
              value={formData.order}
              onChange={handleChange}
              style={fieldStyle}
            />
          </div>
        </>
      )}

      <button
        type="submit"
        style={{
          padding: "10px 20px",
          backgroundColor: "#4CAF50",
          color: "#fff",
          border: "none",
          borderRadius: "5px",
          cursor: "pointer",
          marginTop: "10px",
        }}
      >
        {buttonLoading ? <CircularProgress size={24} color="inherit" /> : submitLabel}
      </button>
    </form>
  );

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h2 style={{ margin: "0 0 6px" }}>Key Offices</h2>
          <p style={{ margin: 0, color: "#666", fontSize: 14 }}>
            Same list as the site Key Offices menu — main offices and their sub items.
          </p>
        </div>
        <button
          onClick={() => {
            setFormData(emptyForm);
            setImgFile(null);
            setShowCreateModal(true);
          }}
          style={{
            padding: "10px 20px",
            backgroundColor: "#4CAF50",
            color: "#fff",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
          }}
        >
          Add Item
        </button>
      </div>

      <div style={{ marginTop: 24 }}>
        {fetchLoading ? (
          <CircularProgress />
        ) : mainItems.length === 0 ? (
          <p style={{ color: "#666" }}>No Key Offices yet.</p>
        ) : (
          mainItems.map((main) => (
            <div
              key={main._id}
              style={{
                background: "#fff",
                borderRadius: 8,
                boxShadow: "0 4px 8px rgba(0,0,0,0.08)",
                padding: 16,
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 12,
                }}
              >
                <div style={{ display: "flex", gap: 14, flex: 1 }}>
                  {main.img ? (
                    <img
                      src={main.img}
                      alt=""
                      style={{
                        width: 96,
                        height: 64,
                        objectFit: "cover",
                        borderRadius: 6,
                        flexShrink: 0,
                      }}
                    />
                  ) : null}
                  <div>
                    <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
                      Main · Order {main.order ?? 0}
                      {main.hideParentLink ? " · parent link hidden" : ""}
                    </div>
                    <h3 style={{ margin: "0 0 4px" }}>{main.name}</h3>
                    <div style={{ fontSize: 13, color: "#555" }}>{main.href || "—"}</div>
                    {main.contactEmail ? (
                      <div style={{ fontSize: 12, color: "#777", marginTop: 4 }}>
                        {main.contactName ? `${main.contactName} · ` : ""}
                        {main.contactEmail}
                      </div>
                    ) : null}
                  </div>
                </div>
                <Actions courseId={main._id} onEdit={handleView} onDelete={handleDelete} />
              </div>

              <div style={{ marginTop: 14, paddingLeft: 12, borderLeft: "3px solid #e5e7eb" }}>
                {(childrenByParent[String(main._id)] || []).length === 0 ? (
                  <div style={{ fontSize: 13, color: "#999" }}>No sub menu items</div>
                ) : (
                  (childrenByParent[String(main._id)] || []).map((child) => (
                    <div
                      key={child._id}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 12,
                        padding: "10px 0",
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 12, color: "#888" }}>
                          Sub · Order {child.order ?? 0}
                        </div>
                        <div style={{ fontWeight: 600 }}>{child.name}</div>
                        <div style={{ fontSize: 13, color: "#555" }}>{child.href || "—"}</div>
                      </div>
                      <Actions
                        courseId={child._id}
                        onEdit={handleView}
                        onDelete={handleDelete}
                      />
                    </div>
                  ))
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <Modal
        isOpen={showCreateModal}
        onRequestClose={() => setShowCreateModal(false)}
        style={customModalStyles}
        contentLabel="Add Key Office Item"
      >
        <h2>Add Item</h2>
        {renderForm(handleCreate, "Create")}
      </Modal>

      <Modal
        isOpen={showEditModal}
        onRequestClose={() => {
          setFormData(emptyForm);
          setImgFile(null);
          setShowEditModal(false);
        }}
        style={customModalStyles}
        contentLabel="Edit Key Office Item"
      >
        <h2>Edit Item</h2>
        {renderForm(handleEdit, "Update")}
      </Modal>
    </div>
  );
};

export default Office;
